import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AxiosError } from "axios";
import { useAuth } from "../auth/AuthContext";

export function LoginPage() {
  const { signIn, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(username, password);
      if (result.requires2fa && result.challenge) {
        setChallenge(result.challenge);
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        setError("Invalid username or password.");
      } else if (err instanceof AxiosError && err.response?.status === 429) {
        setError("Too many attempts. Try again in 15 minutes.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyTwoFactor(challenge!, code);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        setError("Invalid or expired code. Try again.");
      } else {
        setError("Verification failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <form onSubmit={handleVerify} className="bg-white rounded-lg shadow p-8 w-full max-w-sm space-y-4">
          <h1 className="text-xl font-bold text-center text-brand">Two-factor authentication</h1>
          <p className="text-sm text-gray-500 text-center">
            Enter the 6-digit code from your authenticator app.
          </p>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded px-3 py-2">{error}</div>}
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className="w-full border border-gray-300 rounded px-3 py-2 text-center text-2xl tracking-widest"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
          />
          <button
            type="submit"
            disabled={busy || code.length < 6}
            className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button
            type="button"
            onClick={() => { setChallenge(null); setCode(""); setError(null); }}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            Back to sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow p-8 w-full max-w-sm space-y-4"
      >
        <h1 className="text-xl font-bold text-center text-brand">Reliadata.io</h1>
        {error && (
          <div className="bg-red-50 text-red-700 text-sm rounded px-3 py-2">{error}</div>
        )}
        <div>
          <label className="block text-sm text-gray-600 mb-1">Username</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Password</label>
          <input
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-sm text-gray-500">
          No account?{" "}
          <Link to="/signup" className="text-brand hover:underline">
            Create one
          </Link>
        </p>
      </form>
    </div>
  );
}
